import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_TABLE_FILTER_TAG,
  MEDIA_TABLE_FILTER_FOLDER,
  MEDIA_TABLE_FILTER_TYPE,
} from "fleet/constant/media-library";
import { PropTypeMediaEntryTypes } from "fleet/prop-types";
import { PropTypeMediaFilterFields } from "fleet/prop-types";
import { MediaTable } from "fleet/view/media-library/media-table";
import { getMediaEntries } from "fleet/view/media-library/utils";
import PropTypes from "prop-types";
import React from "react";
import { makeGetAllAsArray } from "redux-store";
import { Filter, FILTER_TYPE_SELECT } from "ui/filter";
import { arrayToSelectOptions } from "util/select";

const getAllMediaAsArray = makeGetAllAsArray();

class MediaTableController extends React.PureComponent {
  static get propTypes() {
    const mtPropTypes = MediaTable.propTypes;

    return {
      ...{
        actions: mtPropTypes.actions,
        clickable: mtPropTypes.clickable,
        excludeColumns: mtPropTypes.excludeColumns,
        extraColumns: mtPropTypes.extraColumns,
        formatters: mtPropTypes.formatters,
        getRef: mtPropTypes.getRef,
        highlight: mtPropTypes.highlight,
        options: mtPropTypes.options,
        selectOptions: mtPropTypes.selectOptions,
        toolbarWidth: mtPropTypes.toolbarWidth,
      },
      applications: PropTypes.object,
      contentFolders: PropTypes.object,
      contentGroups: PropTypes.object,
      filterFields: PropTypes.arrayOf(PropTypeMediaFilterFields),
      includeMediaTypes: PropTypes.arrayOf(PropTypeMediaEntryTypes),
      processTableData: PropTypes.func,
      userContents: PropTypes.object,
    };
  }

  static get defaultProps() {
    const mtDefaultProps = MediaTable.defaultProps;

    return {
      ...{
        clickable: mtDefaultProps.clickable,
        formatters: mtDefaultProps.formatters,
      },
      applications: {},
      contentFolders: {},
      contentGroups: {},
      filterFields: [],
      includeMediaTypes: [],
      processTableData: (data) => data,
      userContents: {},
    };
  }

  constructor(props) {
    super(props);

    this.filterApi = null;

    const tableData = this._getTableData(props);
    this.state = {
      tableData,
      filterOptions: this._getFilterOptions(tableData),
      selected: [],
    };

    this._getFilterOptions = this._getFilterOptions.bind(this);
    this.getFilterApi = this.getFilterApi.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.filterApi) {
      return;
    }

    const checkProps = [
      "applications",
      "contentFolders",
      "contentGroups",
      "includeMediaTypes",
      "processTableData",
      "userContents",
    ];

    const propsSame = checkProps.every((name) => {
      return this.props[name] === prevProps[name];
    });

    if (propsSame) {
      return;
    }

    const unfilteredData = this._getTableData(this.props);
    const tableData = this.filterApi.getFilteredData(unfilteredData);
    this.setState({
      tableData,
      filterOptions: this._getFilterOptions(tableData),
      selected: [],
    });
  }

  getFilterApi(filterApi) {
    this.filterApi = filterApi;
  }

  onFilterChange() {
    const unfilteredData = this._getTableData(this.props);
    const tableData = this.filterApi.getFilteredData(unfilteredData);

    this.setState({ tableData });
  }

  _formatFilterOptions(set) {
    return arrayToSelectOptions(Array.from(set));
  }

  _getFilterFields() {
    const { filterFields } = this.props;
    const { filterOptions } = this.state;
    const filters = [];

    if (filterFields.includes(MEDIA_TABLE_FILTER_TYPE)) {
      filters.push({
        label: "Type",
        name: MEDIA_TABLE_FILTER_TYPE,
        type: FILTER_TYPE_SELECT,
        options: filterOptions.friendlyTypes,
      });
    }

    if (filterFields.includes(MEDIA_TABLE_FILTER_TAG)) {
      filters.push({
        label: "Creative Category",
        name: MEDIA_TABLE_FILTER_TAG,
        type: FILTER_TYPE_SELECT,
        options: filterOptions.categories,
      });
    }

    if (filterFields.includes(MEDIA_TABLE_FILTER_FOLDER)) {
      filters.push({
        label: "Folder",
        name: MEDIA_TABLE_FILTER_FOLDER,
        type: FILTER_TYPE_SELECT,
        options: filterOptions.folders,
      });
    }

    return filters;
  }

  _getFilterOptions(tableData) {
    const folders = new Set();
    const friendlyTypes = new Set();
    const tags = new Set();

    tableData.forEach((entry) => {
      const { friendlyType, folderName, tag } = entry;

      folders.add(folderName);
      friendlyTypes.add(friendlyType);
      tags.add(tag);
    });

    return {
      categories: this._formatFilterOptions(tags),
      folders: this._formatFilterOptions(folders),
      friendlyTypes: this._formatFilterOptions(friendlyTypes),
    };
  }

  _getTableData(props) {
    const {
      applications,
      contentFolders,
      contentGroups,
      includeMediaTypes,
      processTableData,
      userContents,
    } = props;

    const source = [
      [MEDIA_ENTRY_APPLICATION, applications],
      [MEDIA_ENTRY_CONTENT_GROUP, contentGroups],
      [MEDIA_ENTRY_FOLDER, contentFolders],
      [MEDIA_ENTRY_USER_CONTENT, userContents],
    ];

    const sources = source.map((s) => {
      const mediaType = includeMediaTypes.find((i) => {
        return i === s[0];
      });

      return mediaType ? getAllMediaAsArray(s[1]) : [];
    });

    const stores = {
      contentFolders,
      contentGroups,
      userContents,
    };

    const entries = getMediaEntries(stores, sources);
    return processTableData(entries);
  }

  _renderToolbar() {
    const filterFields = this._getFilterFields();
    if (filterFields.length === 0) {
      return null;
    }

    return (
      <Filter
        fields={filterFields}
        getApi={this.getFilterApi}
        onFilterChange={this.onFilterChange}
      />
    );
  }

  render() {
    const {
      actions,
      clickable,
      excludeColumns,
      extraColumns,
      formatters,
      getRef,
      highlight,
      options,
      selectOptions,
      toolbarWidth,
    } = this.props;
    const { tableData } = this.state;

    return (
      <MediaTable
        actions={actions}
        clickable={clickable}
        data={tableData}
        excludeColumns={excludeColumns}
        extraColumns={extraColumns}
        formatters={formatters}
        getRef={getRef}
        highlight={highlight}
        options={options}
        selectOptions={selectOptions}
        toolbar={this._renderToolbar()}
        toolbarWidth={toolbarWidth}
      />
    );
  }
}

export { MediaTableController };
